import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "utils/redux";
import { useParams } from "react-router-dom";

import { CreateRREModal, CreateRRETinModal } from "components/Modals";
import { setShowAddModal, setCurrentSection } from "reducers/main";
import { useGetRreByIdQuery } from "services/rre-api";
import {
  setRRE,
  setShowAddRRETagModal,
  setShowDownloadModal,
  setShowUploadModal,
  setShowSendCMSFileModal,
} from "reducers/rre";

import "./RREPageContainer.scss";
import UploadClaimAndQueryModal from "components/Modals/UploadClaimAndQueryModal";
import DownloadClaimAndQueryModal from "components/Modals/DownloadClaimAndQueryModal";
import CreateOrEditRRETagModal from "components/Modals/CreateOrEditRRETagModal";
import SendCMSFileModal from "components/Modals/SendCMSFileModal";
import RREDocuments from "./RREDocumentsDrawerTable";
import { SECTION } from "constants/SettingsConstants";

type RREContainerPropTypes = {
  header?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

const RREPageContainer = ({
  header,
  footer,
  children,
}: RREContainerPropTypes) => {
  const dispatch = useDispatch();
  const showAddModal = useSelector((state) => state.main.showAddModal);
  const showUploadModal = useSelector((state) => state.rre.showUploadModal);
  const showDownloadModal = useSelector((state) => state.rre.showDownloadModal);
  const showSendCMSFileModal = useSelector(
    (state) => state.rre.showSendCMSFileModal,
  );
  const showAddRRETagModal = useSelector(
    (state) => state.rre.showAddRRETagModal,
  );

  const usingTinModal = window.location.href.includes("tins");
  const { rreId } = useParams<{ rreId?: string }>();

  const { data: rre } = useGetRreByIdQuery({ rreId }, { skip: !rreId });

  useEffect(() => {
    dispatch(setRRE(rre));
  }, [dispatch, rre]);

  useEffect(() => {
    dispatch(setCurrentSection(SECTION.SECTION_111_RRES));
  }, [dispatch]);

  const modal = useMemo(() => {
    if (showAddModal) {
      return usingTinModal ? (
        <CreateRRETinModal
          isOpen={showAddModal}
          onClose={() => {
            dispatch(setShowAddModal(false));
          }}
        />
      ) : (
        <CreateRREModal
          isOpen={showAddModal}
          onClose={() => {
            dispatch(setShowAddModal(false));
          }}
        />
      );
    } else if (showUploadModal) {
      return (
        <UploadClaimAndQueryModal
          isOpen={showUploadModal}
          onClose={() => {
            dispatch(setShowUploadModal(false));
          }}
        />
      );
    } else if (showDownloadModal) {
      return (
        <DownloadClaimAndQueryModal
          isOpen={showDownloadModal}
          onClose={() => {
            dispatch(setShowDownloadModal(false));
          }}
        />
      );
    } else if (showAddRRETagModal) {
      return (
        <CreateOrEditRRETagModal
          isOpen={showAddRRETagModal}
          onClose={() => {
            dispatch(setShowAddRRETagModal(false));
          }}
          isAddition={showAddRRETagModal}
        />
      );
    } else if (showSendCMSFileModal) {
      return (
        <SendCMSFileModal
          isOpen={showSendCMSFileModal}
          onClose={() => {
            dispatch(setShowSendCMSFileModal(false));
          }}
        />
      );
    }
  }, [
    showAddModal,
    showUploadModal,
    showDownloadModal,
    showAddRRETagModal,
    showSendCMSFileModal,
    usingTinModal,
    dispatch,
  ]);

  const rreDocumentsDrawer = useMemo(() => {
    if (!rreId) {
      return null;
    }
    return <RREDocuments rreId={Number(rreId)} />;
  }, [dispatch, rreId]);

  return (
    <>
      {modal}
      {rreDocumentsDrawer}
      <div className="RREPageContainer">
        {header && <div className="RREPageContainer__Header">{header}</div>}
        <div className="RREPageContainer__Forms">{children}</div>
        {footer && <div className="RREPageContainer__Footer">{footer}</div>}
      </div>
    </>
  );
};

export default RREPageContainer;
